import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEO, { SITE_URL } from '@/components/SEO';
import { useAboutData } from '@/hooks/useTranslatedData';
import { useTranslation } from 'react-i18next';

const aboutSchema = {
  '@type': 'AboutPage',
  name: '品牌故事 | 膳兜兜 foodgogo',
  description: '了解膳兜兜foodgogo的品牌起源、核心理念与愿景，探索食品实验室如何催生新一代轻养生健康食品品牌。',
  url: `${SITE_URL}/about`,
  mainEntity: {
    '@type': 'Organization',
    name: '膳兜兜 foodgogo',
    url: `${SITE_URL}/`,
    brand: {
      '@type': 'Brand',
      name: '膳兜兜 foodgogo',
    },
  },
};

export default function About() {
  const { t } = useTranslation('common');
  const data = useAboutData();

  return (
    <div className="min-h-screen">
      <SEO
        title="品牌故事 | 膳兜兜 foodgogo"
        description="了解膳兜兜foodgogo的品牌起源、核心理念与愿景，探索食品实验室如何催生新一代轻养生健康食品品牌。"
        keywords="膳兜兜,品牌故事,foodgogo,健康食品品牌,轻养生,食品实验室"
        schema={aboutSchema}
      />
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative pt-24 md:pt-32 pb-16 md:pb-20 overflow-hidden">
          {/* KV Background */}
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: `url('https://readdy.ai/api/search-image?query=Warm%20editorial%20photography%20of%20modern%20food%20brand%20creative%20workspace%20scattered%20botanical%20ingredients%20and%20design%20sketches%20soft%20warm%20cream%20and%20sage%20green%20tones%20minimalist%20aesthetic%20with%20organic%20textures%20gentle%20natural%20light%20premium%20health%20food%20brand%20atmosphere%20no%20text%20clean%20composition%20shallow%20depth%20of%20field&width=1920&height=600&seq=about-hero-kv-001&orientation=landscape')`
            }}
          />
          {/* Overlay */}
          <div className="absolute inset-0 bg-gradient-to-b from-brand-cream/95 via-brand-cream/85 to-brand-cream/95" />

          <div className="relative z-10 w-full px-6 md:px-10 lg:px-16 max-w-4xl mx-auto text-center">
            <span className="inline-block text-xs font-semibold tracking-wider text-brand-gray uppercase mb-4">
              {data.hero.subtitle}
            </span>
            <h1 className="text-4xl md:text-6xl font-bold text-brand-charcoal mb-6 leading-tight">
              {data.hero.title}
            </h1>
            <p className="text-base md:text-lg text-brand-gray leading-relaxed">
              {data.hero.description}
            </p>
          </div>
        </section>

        {/* Origin */}
        <section className="py-16 md:py-24 bg-white">
          <div className="w-full px-6 md:px-10 lg:px-16 max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-16 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-brand-charcoal mb-6">
                  {data.origin.title}
                </h2>
                <p className="text-base text-brand-gray leading-relaxed mb-6">
                  {data.origin.story}
                </p>
              </div>
              <div className="rounded-2xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20food%20science%20laboratory%20interior%20with%20researchers%20working%2C%20glass%20beakers%20and%20equipment%2C%20natural%20botanical%20ingredients%20on%20workbench%2C%20warm%20soft%20lighting%2C%20clean%20minimal%20aesthetic%2C%20mint%20green%20and%20white%20color%20palette%2C%20professional%20editorial%20photography%2C%20shallow%20depth%20of%20field&width=800&height=500&seq=50&orientation=landscape"
                  alt={data.origin.title}
                  className="w-full h-72 md:h-80 object-cover"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Philosophy */}
        <section className="py-16 md:py-24 bg-brand-sand">
          <div className="w-full px-6 md:px-10 lg:px-16 max-w-6xl mx-auto">
            <div className="text-center mb-12 md:mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-brand-charcoal mb-3">
                {data.philosophy.title}
              </h2>
              <p className="text-lg text-brand-green font-medium">
                {data.philosophy.subtitle}
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
              {data.philosophy.points.map((point) => (
                <div key={point.title} className="bg-white rounded-2xl p-6 md:p-8 text-center">
                  <div className="w-12 h-12 rounded-full bg-brand-mint-light flex items-center justify-center mx-auto mb-4">
                    <i className="ri-database-2-line text-brand-mint-dark text-xl"></i>
                  </div>
                  <h3 className="text-base font-semibold text-brand-charcoal mb-2">
                    {point.title}
                  </h3>
                  <p className="text-sm text-brand-gray leading-relaxed">
                    {point.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Vision */}
        <section className="py-16 md:py-24 bg-brand-cream">
          <div className="w-full px-6 md:px-10 lg:px-16 max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-16 items-center">
              <div className="order-2 lg:order-1 rounded-2xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Young%20diverse%20group%20of%20healthy%20active%20people%20enjoying%20outdoor%20activity%20with%20modern%20healthy%20snacks%2C%20warm%20golden%20hour%20lighting%2C%20lifestyle%20photography%2C%20green%20nature%20background%2C%20joyful%20energetic%20mood%2C%20premium%20health%20food%20brand%20editorial%20style&width=800&height=450&seq=51&orientation=landscape"
                  alt={data.vision.title}
                  className="w-full h-72 md:h-80 object-cover"
                  loading="lazy"
                />
              </div>
              <div className="order-1 lg:order-2">
                <h2 className="text-3xl md:text-4xl font-bold text-brand-charcoal mb-3">
                  {data.vision.title}
                </h2>
                <p className="text-lg text-brand-green font-medium mb-6">
                  {data.vision.subtitle}
                </p>
                <p className="text-base text-brand-gray leading-relaxed">
                  {data.vision.text}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Name Meaning */}
        <section className="py-16 md:py-24 bg-brand-green">
          <div className="w-full px-6 md:px-10 lg:px-16 max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">
              {data.nameMeaning.title}
            </h2>
            <p className="text-lg text-white/70 mb-10">
              {data.nameMeaning.subtitle}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-10">
              {data.nameMeaning.chars.map((item) => (
                <div key={item.char} className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 md:p-8">
                  <span className="block text-4xl font-bold text-brand-mint mb-3">
                    {item.char}
                  </span>
                  <p className="text-sm text-white/80 leading-relaxed">
                    {item.explain}
                  </p>
                </div>
              ))}
            </div>
            <div className="inline-block">
              <span className="block text-2xl md:text-3xl font-bold text-brand-coral mb-2">
                {data.nameMeaning.english}
              </span>
              <p className="text-sm text-white/70">
                {data.nameMeaning.englishMeaning}
              </p>
            </div>
          </div>
        </section>

        {/* Timeline */}
        <section className="py-16 md:py-24 bg-white">
          <div className="w-full px-6 md:px-10 lg:px-16 max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-brand-charcoal mb-12 text-center">
              {data.timeline.title}
            </h2>
            <div className="relative">
              <div className="absolute left-4 md:left-1/2 md:-translate-x-px top-0 bottom-0 w-0.5 bg-brand-gray-pale" />
              {data.timeline.events.map((item, index) => (
                <div key={item.year} className={`relative flex items-start gap-6 md:gap-0 mb-10 ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                  <div className={`flex-1 ${index % 2 === 0 ? 'md:text-right md:pr-10' : 'md:text-left md:pl-10'}`}>
                    <span className="block text-xl font-bold text-brand-mint-dark mb-1">
                      {item.year}
                    </span>
                    <span className="block text-sm text-brand-gray">{item.event}</span>
                  </div>
                  <div className="absolute left-4 md:left-1/2 -translate-x-1/2 w-4 h-4 rounded-full bg-brand-mint-dark border-4 border-white shadow-sm z-10" />
                  <div className="flex-1 hidden md:block" />
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}